import { z } from 'zod';

/**
 * SIL-2055 (SAPI P1-A): `data_subscription` wire DTOs, tier ranking and the
 * `requireDataTier` entitlement check (migration 1118).
 *
 * The tier/status enums mirror the DB CHECK constraints by hand. Tier
 * comparison is ALWAYS through `DATA_TIER_RANK` (an int map), never string
 * comparison. `requireDataTier` is the single decision function: the API
 * (Cognito session path AND, from P1-B, the API-key path) and the UI gates
 * must both call it so the two cannot disagree.
 */
export const DATA_TIERS = ['free', 'pro', 'data_feed', 'enterprise'] as const;
export const dataTierSchema = z.enum(DATA_TIERS);
export type DataTier = z.infer<typeof dataTierSchema>;

export const DATA_SUBSCRIPTION_STATUSES = ['active', 'suspended', 'cancelled'] as const;
export const dataSubscriptionStatusSchema = z.enum(DATA_SUBSCRIPTION_STATUSES);
export type DataSubscriptionStatus = z.infer<typeof dataSubscriptionStatusSchema>;

export const DATA_TIER_RANK: Readonly<Record<DataTier, number>> = Object.freeze({
  free: 0,
  pro: 1,
  data_feed: 2,
  enterprise: 3,
});

/**
 * Per-tier default limits, applied by the ops UI/API when the operator does
 * not supply explicit values (the DB stores explicit values only). Pro is the
 * pilot default (owner decision 2026-10-16): 20 calls/min, 500 calls/day.
 * Non-pro values are placeholders pending pricing; they are NOT a published
 * contract.
 */
export const DATA_TIER_DEFAULT_LIMITS: Readonly<
  Record<DataTier, { rateLimitPerMin: number; dailyQuota: number }>
> = Object.freeze({
  free: { rateLimitPerMin: 5, dailyQuota: 50 },
  pro: { rateLimitPerMin: 20, dailyQuota: 500 },
  data_feed: { rateLimitPerMin: 60, dailyQuota: 5000 },
  enterprise: { rateLimitPerMin: 120, dailyQuota: 20000 },
});

export const dataSubscriptionSchema = z.object({
  id: z.string().uuid(),
  customerOrgId: z.string().uuid(),
  tier: dataTierSchema,
  status: dataSubscriptionStatusSchema,
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  rateLimitPerMin: z.number().int().positive(),
  dailyQuota: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DataSubscription = z.infer<typeof dataSubscriptionSchema>;

/** GET responses: `subscription` is null when the org has no active/suspended row. */
export const dataSubscriptionResponseSchema = z.object({
  subscription: dataSubscriptionSchema.nullable(),
});
export type DataSubscriptionResponse = z.infer<typeof dataSubscriptionResponseSchema>;

const isoDate = z.string().refine((v) => !Number.isNaN(Date.parse(v)), 'invalid date');

/**
 * Ops upsert body (PUT /api/customers/:id/data-subscription). Limits are
 * optional: omitted values take the tier default on create and keep the
 * current value on update.
 */
export const dataSubscriptionWriteSchema = z
  .object({
    tier: dataTierSchema,
    status: dataSubscriptionStatusSchema,
    startsAt: isoDate.optional(),
    endsAt: isoDate.nullable().optional(),
    rateLimitPerMin: z.number().int().positive().optional(),
    dailyQuota: z.number().int().positive().optional(),
  })
  .refine(
    (v) => v.startsAt == null || v.endsAt == null || Date.parse(v.endsAt) > Date.parse(v.startsAt),
    { message: 'endsAt must be after startsAt', path: ['endsAt'] },
  );
export type DataSubscriptionWrite = z.infer<typeof dataSubscriptionWriteSchema>;

export type DataTierDenyReason =
  | 'no_subscription'
  | 'not_active'
  | 'not_started'
  | 'expired'
  | 'tier_too_low'
  | 'unknown_tier';

export type DataTierDecision = { ok: true } | { ok: false; reason: DataTierDenyReason };

/** Minimal shape the check needs; both the DTO and a raw DB row satisfy it. */
export interface DataEntitlementInput {
  tier: string;
  status: string;
  startsAt: Date | string;
  endsAt: Date | string | null;
}

function ms(v: Date | string): number {
  return v instanceof Date ? v.getTime() : Date.parse(v);
}

/**
 * `requireDataTier(min)` returns the entitlement check for "at least tier
 * `min`". Fail-closed: a missing subscription, any status but `active`, a
 * window that has not started or has ended, an unparseable date, or an
 * unrecognized tier all deny.
 */
export function requireDataTier(min: DataTier) {
  const minRank = DATA_TIER_RANK[min];
  return (
    sub: DataEntitlementInput | null | undefined,
    now: Date = new Date(),
  ): DataTierDecision => {
    if (sub == null) return { ok: false, reason: 'no_subscription' };
    if (sub.status !== 'active') return { ok: false, reason: 'not_active' };
    if (!Object.prototype.hasOwnProperty.call(DATA_TIER_RANK, sub.tier)) {
      return { ok: false, reason: 'unknown_tier' };
    }
    const start = ms(sub.startsAt);
    if (Number.isNaN(start) || start > now.getTime()) return { ok: false, reason: 'not_started' };
    if (sub.endsAt != null) {
      const end = ms(sub.endsAt);
      if (Number.isNaN(end) || end <= now.getTime()) return { ok: false, reason: 'expired' };
    }
    if (DATA_TIER_RANK[sub.tier as DataTier] < minRank) return { ok: false, reason: 'tier_too_low' };
    return { ok: true };
  };
}
